import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import PropTypes from "prop-types"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import { NumericFormat } from "react-number-format"
import { isMobile, withOrientationChange } from "react-device-detect"

import { getConfig } from "@/constants/config"
import { getShortenedUrl, sanitizeTextForAnalytics } from "@/utils/helper"
import { addToCartItem } from "@/utils/addTocart"
import { getEventInfo } from "@/components/ProductDetail/v3/DesignServices/analytics"

import { addToCart } from "@/store/features/cartSlice"
import { showPreviewCart, showToast } from "@/store/features/genericSlice"

import Button from "@/components/core/Button/Button"
import styles from "@/components/ProductDetail/v3/DesignServices/index.module.scss"

const DesignServices = props => {
  const {
    width = 0,
    staticTexts = {},
    designServiceAPIData = [],
    locale = "",
    currencyCode = "",
    currencySign = "",
    designServiceListData = {},
    pdpType = "",
    replaceAddToCart = false,
    replaceAddToCartCtaLink = "",
    replaceAddToCartCtaLabel = "",
    linkNewTab = false,
    disableEcommerce = false,
    cart = {},
    sku = "",
  } = props
  const dispatch = useDispatch()
  const quantity = 1
  const [general, setGeneral] = useState({})
  const [cartUrl, setCartUrl] = useState("")
  const [ctaLink, setCtaLink] = useState("")

  useEffect(() => {
    getConfig().then(config => {
      const { general } = config
      setGeneral(general)
    })
  }, [])

  useEffect(() => {
    if (!_isEmpty(general)) {
      getShortenedUrl(general.cartLandingPagePath ?? "/").then(res =>
        setCartUrl(res)
      )
    }
  }, [general])

  useEffect(() => {
    if (designServiceListData?.ctaLink) {
      getShortenedUrl(designServiceListData.ctaLink).then(res =>
        setCtaLink(res)
      )
    }
  }, [designServiceListData.ctaLink])

  const addtoCart = item => {
    const { quantity } = item
    const { serviceItemsAdded, yourCart } = staticTexts
    const itemCountMessage = `${quantity} ${serviceItemsAdded} `
    const ToasterMsg = `${itemCountMessage}<a href=${cartUrl}>${yourCart}</a>.`
    addToCartItem([
      {
        ...item,
        isBundle: false,
        backorder: false,
      },
    ]).then(response => {
      const body = {
        actions: response,
      }
      dispatch(addToCart(body))
        .unwrap()
        .then(res => {
          if (res) {
            if (isMobile) {
              dispatch(
                showToast({
                  message: ToasterMsg,
                  isVisible: true,
                })
              )
            } else {
              dispatch(showPreviewCart())
            }
          }
        })
    })
  }

  let percentDiscount
  let percentType
  const designServiceTitle = _get(
    designServiceAPIData[0],
    `name[${locale}]`,
    false
  )
  const masterVariant = _get(designServiceAPIData[0], "masterVariant", [])
  const masterVariantPrice = _get(masterVariant, "prices", [])
  const designServicePrice = Number(
    _get(
      masterVariantPrice.find(item => item.value.currencyCode === currencyCode),
      "value.centAmount",
      0
    ) / 100
  ).toFixed(2)

  const isDiscount = _get(masterVariantPrice[0], "discounted", {})
  const designServiceDiscountedPrice = !_isEmpty(isDiscount)
    ? Number(
        _get(
          masterVariantPrice.find(
            item => item.discounted.value.currencyCode === currencyCode
          ),
          "discounted.value.centAmount",
          0
        ) / 100
      ).toFixed(2)
    : 0

  if (isDiscount?.discount?.obj?.value.type === "absolute") {
    percentDiscount =
      designServiceDiscountedPrice > 0 &&
      Number(
        _get(
          masterVariantPrice.find(
            item => item.discounted.value.currencyCode === currencyCode
          ),
          "discounted.discount.obj.value.money[0].centAmount",
          0
        ) / 100
      )
    percentType = "absolute"
  } else {
    percentDiscount =
      designServiceDiscountedPrice > 0 &&
      Number(
        _get(
          masterVariantPrice.find(
            item => item.discounted.value.currencyCode === currencyCode
          ),
          "discounted.discount.obj.value.permyriad",
          0
        ) / 100
      )
    percentType = "relative"
  }

  const masterVariantAttributes = _get(masterVariant, "attributes", [])
  const ProductWebFeatures = _get(
    masterVariantAttributes.find(attr => attr.name === "ProductWebFeatures"),
    "value",
    []
  )

  let desc = ""
  ProductWebFeatures.slice(0, 3).forEach(item => {
    desc += item[locale]
    if (desc && desc[desc.length - 1] !== ".") {
      desc += ". "
    }
  })

  const customerFacingSKU = _get(
    masterVariantAttributes.find(attr => attr.name === "CustomerFacingSKU"),
    "value",
    []
  )
  const productBrand = _get(
    masterVariantAttributes.find(attr => attr.name === "CustomerFacingBrand"),
    "value",
    []
  )

  const discountPriceState =
    designServiceDiscountedPrice > 0
      ? "percentOff|" + percentDiscount
      : "regular price"

  const handleAddToCart = () => {
    addtoCart({ sku: sku, quantity: quantity, brand: productBrand })
  }

  return (
    <div className={styles.designServicesWrapper}>
      <div className="pdp-design-services">
        <div className="pdp-design-services__left">
          <div className="pdp-design-services__title">
            <h3>
              {designServiceTitle
                ? designServiceTitle
                : staticTexts.bathroomDesignService}
            </h3>
          </div>
        </div>
        <div className="pdp-design-services__right">
          <div className="pdp-design-services__price">
            {designServicePrice && (
              <div className="pdp-design-services__original-price">
                <NumericFormat
                  value={
                    designServiceDiscountedPrice > 0
                      ? designServiceDiscountedPrice
                      : designServicePrice
                  }
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={currencySign}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </div>
            )}
            {designServiceDiscountedPrice > 0 && (
              <div className="pdp-design-services__discount-price">
                <p className="pdp-design-services__percent-discount">
                  {percentType === "absolute"
                    ? currencySign + percentDiscount
                    : percentDiscount + "%"}{" "}
                  {staticTexts.percentOff}
                </p>
                <NumericFormat
                  value={designServicePrice}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={currencySign}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={`pdp-design-services__left ${
            width < 768 || (width >= 1024 && width <= 1600)
              ? "--full-width"
              : ""
          }`}
        >
          <div className="pdp-design-services__description">
            <ul>
              {ProductWebFeatures.slice(0, 3).map((item, i) => (
                <li key={i} className="pdp-design-services__list-item">
                  {item[locale]}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div
          className={`pdp-design-services__right ${
            width < 768 || (width >= 1024 && width <= 1600)
              ? "--full-width"
              : ""
          }`}
        >
          <div className="pdp-design-services__button-tab">
            <a
              href={ctaLink}
              className="pdp-design-services__learn-more gbh-data-layer"
              target={designServiceListData.newTab ? "_blank" : "_self"}
              aria-label={staticTexts.ariaLabel.designServiceLearnMore}
              data-gbh-data-layer={getEventInfo(
                "learn more",
                designServiceListData.ctaLink
              )}
              data-gbh-data-layer-custom={`{"productInfo":{"productBrand":"${general?.siteName?.toLowerCase()}","description":"${
                sanitizeTextForAnalytics(desc) || "n/a"
              }","frequency":"n/a","globalPromotionPrice":"${
                designServiceDiscountedPrice > 0
                  ? designServiceDiscountedPrice
                  : designServicePrice
              }","isRecommended":"n/a","isSendNow":"n/a","isSubscription":"n/a","priceState":"${discountPriceState}","productBasePrice":"${
                designServicePrice || "n/a"
              }","productCategory":"design services","productColor":"n/a","productCoupondiscount":"n/a","productID":"${
                customerFacingSKU || "n/a"
              }","productName":"${
                sanitizeTextForAnalytics(staticTexts.bathroomDesignService) ||
                "n/a"
              }","productPartnerBuyNow":"n/a","productRoom":"design services","productSalePrice":"${
                designServiceDiscountedPrice > 0
                  ? designServiceDiscountedPrice
                  : designServicePrice
              }","productSaleable":"true","productStatus":"in stock","productSuperSku":false,"productTileName":"${
                sanitizeTextForAnalytics(staticTexts.bathroomDesignService) ||
                "n/a"
              }","quantity":1,"defaultImageName":"n/a","ratings":"n/a","numberOfReviews":"n/a","pdpType":"${pdpType}","productFindingMethod":"n/a","productCollectionsName":"design services"}}`}
              rel="noreferrer"
            >
              {designServiceListData.ctaLabel}
            </a>
            {replaceAddToCart ? (
              <Button
                role="button"
                ariaLabel={replaceAddToCartCtaLabel}
                label={replaceAddToCartCtaLabel}
                size="small"
                type="primary"
                to={replaceAddToCartCtaLink}
                target={linkNewTab ? "_blank" : "_self"}
              />
            ) : (
              ""
            )}

            {!disableEcommerce ? (
              <Button
                className={"gbh-data-layer"}
                data-gbh-data-layer={getEventInfo(
                  "add to cart",
                  `${window.location.origin}${window.location.pathname}`
                )}
                data-gbh-data-layer-custom={`{"page":{"cart":{"cartID":"${
                  cart?.id
                }"}},"productInfo":{"productBrand":"${general?.siteName?.toLowerCase()}","description":"${
                  sanitizeTextForAnalytics(desc) || "n/a"
                }","frequency":"n/a","globalPromotionPrice":"${
                  designServiceDiscountedPrice > 0
                    ? designServiceDiscountedPrice
                    : designServicePrice
                }","isRecommended":"n/a","isSendNow":"n/a","isSubscription":"n/a","priceState":"${discountPriceState}","productBasePrice":"${
                  designServicePrice || "n/a"
                }","productCategory":"design services","productColor":"n/a","productCoupondiscount":"n/a","productID":"${
                  customerFacingSKU || "n/a"
                }","productName":"${
                  sanitizeTextForAnalytics(staticTexts.bathroomDesignService) ||
                  "n/a"
                }","productPartnerBuyNow":"n/a","productRoom":"design services","productSalePrice":"${
                  designServiceDiscountedPrice > 0
                    ? designServiceDiscountedPrice
                    : designServicePrice
                }","productSaleable":"true","productStatus":"in stock","productSuperSku":false,"productTileName":"${
                  sanitizeTextForAnalytics(staticTexts.bathroomDesignService) ||
                  "n/a"
                }","quantity":1,"defaultImageName":"n/a","ratings":"n/a","numberOfReviews":"n/a","pdpType":"${pdpType}","productFindingMethod":"n/a","productCollectionsName":"design services"}}`}
                role="button"
                ariaLabel={staticTexts.ariaLabel.addProductService}
                label={staticTexts.addService}
                size="small"
                type="primary"
                onClick={handleAddToCart}
                useDefaultClass
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

DesignServices.defaultProps = {
  description: [],
  currencySign: "",
  price: "",
  staticTexts: {
    learnMore: "",
    addToCart: "",
  },
}

DesignServices.propTypes = {
  description: PropTypes.arrayOf(PropTypes.string).isRequired,
  currencySign: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  staticTexts: PropTypes.shape({
    learnMore: PropTypes.string.isRequired,
    addToCart: PropTypes.string.isRequired,
  }),
}

export default withOrientationChange(DesignServices)
